import { memo } from 'react';
import { ChartIcons, ClipBoardIcons, FilterIcons } from '../../../../../../icons/icons';
import { Box, ButtonBase, Divider, Stack, Typography } from '@mui/material';

interface FilterButtonGroupProps {
  onJDClick: () => void;
  onPreQualClick: () => void;
  onCCAClick: () => void;
  isJDActive: boolean;
  isPreQualActive: boolean;
  isCCAActive: boolean;
}

const activeStyle = {
  bgcolor: 'white',
  boxShadow: '0 4px 6px rgba(16,185,129,0.05)',
  ring: '1px solid rgba(226,232,240,0.5)',
};

export const FilterButtonGroup = memo<FilterButtonGroupProps>(({
  onJDClick,
  onPreQualClick,
  onCCAClick,
  isJDActive,
  isPreQualActive,
  isCCAActive,
}) => {
  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{
        overflow: 'hidden',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: '#e2e8f0',
        bgcolor: 'rgba(241,245,249,0.7)',
        p: 0.25,
        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
      }}
    >
      {/* JD Filter Button */}
      <ButtonBase
        onClick={onJDClick}
        title="Job Description Filters"
        sx={{
          display: 'flex',
          minHeight: 32,
          alignItems: 'center',
          gap: 0.75,
          borderRadius: '6px',
          px: 1,
          py: 0.5,
          transition: 'all 0.3s',
          ...(isJDActive
            ? {
                bgcolor: 'white',
                color: '#059669',
                boxShadow: '0 4px 6px rgba(16,185,129,0.05)',
                outline: '1px solid rgba(226,232,240,0.5)',
              }
            : {
                color: '#64748b',
                '&:hover': { color: '#334155', bgcolor: 'rgba(255,255,255,0.4)' },
              }),
        }}
      >
        <FilterIcons size={12} color={isJDActive ? '#10b981' : '#94a3b8'} />
        <Typography
          sx={{
            fontSize: '10px',
            fontWeight: 900,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          JD
        </Typography>
      </ButtonBase>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 'auto', height: 16, alignSelf: 'center' }} />

      {/* PreQual Filter Button */}
      <ButtonBase
        onClick={onPreQualClick}
        title="Pre-Qualification Filters"
        sx={{
          display: 'flex',
          minHeight: 32,
          alignItems: 'center',
          gap: 0.75,
          borderRadius: '6px',
          px: 1,
          py: 0.5,
          transition: 'all 0.3s',
          ...(isPreQualActive
            ? {
                bgcolor: 'white',
                color: '#059669',
                boxShadow: '0 4px 6px rgba(16,185,129,0.05)',
                outline: '1px solid rgba(226,232,240,0.5)',
              }
            : {
                color: '#64748b',
                '&:hover': { color: '#334155', bgcolor: 'rgba(255,255,255,0.4)' },
              }),
        }}
      >
        <ClipBoardIcons size={12} color={isPreQualActive ? '#10b981' : '#94a3b8'} />
        <Typography
          sx={{
            fontSize: '10px',
            fontWeight: 900,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          PreQual
        </Typography>
      </ButtonBase>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.25, my: 'auto', height: 16, alignSelf: 'center' }} />

      {/* CCA Filter Button */}
      <ButtonBase
        onClick={onCCAClick}
        title="CCA Filters"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          px: 1.5,
          py: 0.75,
          borderRadius: '6px',
          transition: 'all 0.3s',
          ...(isCCAActive
            ? {
                bgcolor: 'white',
                color: '#7c3aed',
                boxShadow: '0 4px 6px rgba(124,58,237,0.05)',
                outline: '1px solid rgba(226,232,240,0.5)',
              }
            : {
                color: '#64748b',
                '&:hover': { color: '#334155', bgcolor: 'rgba(255,255,255,0.4)' },
              }),
        }}
      >
        <ChartIcons size={14} color={isCCAActive ? '#8b5cf6' : '#94a3b8'} />
        <Typography
          sx={{
            fontSize: '10px',
            fontWeight: 900,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
          }}
        >
          CCA
        </Typography>
      </ButtonBase>
    </Stack>
  );
});

FilterButtonGroup.displayName = 'FilterButtonGroup';
